import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Grid, useTheme } from '@mui/material';
import ShippingBox from '@square-icons/react/24/Misc/shipping-box';
import { MarketButton, MarketList, MarketRow } from '@market/react';
import { useOrderDetails } from 'components/context/order_details';
import { useCheckoutFlow } from 'components/context/checkout_flow';
import { useUserJourneys } from 'components/context/user_journeys';
import {
  onCheckoutPageClick,
  onCheckoutPageView,
  onCheckoutPageViewV2,
  pushHistoryWithSearchParams,
} from 'components/pages/checkout/utilities';
import ShippingRate from 'components/pages/checkout/shipping_method/shipping_rate';
import PageLoader from 'components/common/page_loader';
import {
  SHIPPING_DISCLAIMER,
  SUSTAINABILITY,
  isFeatureEnabled,
} from 'utilities/features';
import { isDashboardMode } from 'helpers/search_parameter_helpers';
import DashboardModeFooter from 'components/common/dashboard_mode_footer';
import { shopFooterDisclaimers } from 'utilities/disclaimers';
import { COMPONENT_NAMES } from 'constants/tracking';
import {
  FrictionSignal,
  FrustrationSignal,
  UserJourneyName,
} from 'constants/user_journeys';

const ShippingMethod = () => {
  const theme = useTheme();
  const history = useHistory();
  const { t } = useTranslation();

  const { checkoutClient, orderData, orderRefresh } = useCheckoutFlow();
  const { trackingData } = useOrderDetails();
  const { userJourneyTracker } = useUserJourneys();

  const [shippingRates, setShippingRates] = useState([]);
  const [processing, setProcessing] = useState(false);

  const fetchShippingRates = () => {
    setProcessing(true);
    checkoutClient
      .fetchShippingRates()
      .then((data) => setShippingRates(data))
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.ShippingMethodStepShippingRatesFetchError,
        });
      })
      .finally(() => setProcessing(false));
  };

  const selectShippingRate = (id) => {
    setProcessing(true);
    checkoutClient
      .selectShippingRate(id)
      .then((data) => {
        orderRefresh().catch(() => {
          userJourneyTracker.addFrictionSignal({
            userJourneyName: UserJourneyName.ShopPlaceOrder,
            frictionSignal: FrictionSignal.ShippingMethodStepOrderRefreshError,
          });
        });
        setShippingRates(data);
      })
      .catch(() => {
        userJourneyTracker.addFrustrationSignal({
          userJourneyName: UserJourneyName.ShopPlaceOrder,
          frustrationSignal:
            FrustrationSignal.ShippingMethodStepShippingRateSelectError,
        });
      })
      .finally(() => setProcessing(false));
  };

  useEffect(() => {
    fetchShippingRates();
    onCheckoutPageView('Shipping Method', trackingData);
    onCheckoutPageViewV2({
      componentName: COMPONENT_NAMES.APPS.CHECKOUT.SHIPPING_SELECTION,
      additionalParameters: trackingData,
    });
  }, []);

  if (shippingRates.length === 0) {
    return <PageLoader />;
  }

  return (
    <>
      <h2>{t('checkout.shipping.title')}</h2>
      <p style={{ margin: theme.spacing(1, 0) }}>
        {t('checkout.shipping.notice')}
      </p>
      {isFeatureEnabled(SUSTAINABILITY) && (
        <MarketRow>
          <ShippingBox slot="leading-accessory" style={{ fill: '#00B23B' }} />
          <label slot="label" style={{ color: '#00B23B' }}>
            {t('checkout.shipping.sustainability')}
          </label>
        </MarketRow>
      )}
      <MarketList
        {...(processing && { disabled: true })}
        interactive
        onMarketListSelectionsDidChange={(event) =>
          selectShippingRate(event.detail.newSelectionValue)
        }
      >
        {shippingRates.map((rate) => (
          <ShippingRate key={rate.id} rate={rate} />
        ))}
      </MarketList>
      {isFeatureEnabled(SHIPPING_DISCLAIMER) && (
        <p
          className="paragraph-10"
          dangerouslySetInnerHTML={{
            __html: t('checkout.shipping.disclaimer'),
          }}
        />
      )}
      <Grid container justifyContent="flex-end">
        <Grid item xs={12} md={6}>
          <MarketButton
            {...(processing && { disabled: true })}
            id="continue-to-payment-btn"
            rank="primary"
            onClick={() => {
              onCheckoutPageClick({
                eventDesc: 'Continue to payment methods',
                componentName: COMPONENT_NAMES.APPS.CHECKOUT.SHIPPING_SELECTION,
                actionItem: 'continue_to_payment_method',
                trackingData,
              });

              pushHistoryWithSearchParams(history, '/payment_option');
            }}
            style={{ width: '100%' }}
          >
            {Number.parseFloat(orderData.payment_due.amount) === 0
              ? t('common.continue')
              : t('checkout.label.continueToPayment')}
          </MarketButton>
        </Grid>
      </Grid>
      {isDashboardMode() && (
        <DashboardModeFooter disclaimers={shopFooterDisclaimers()} />
      )}
    </>
  );
};

export default ShippingMethod;
